import { ABILITY_KINDS } from './abilities.js';
import { bossBodyCells } from './boss.js';
export const DEFAULT_RULES = Object.freeze({
    width: 8,
    height: 16,
    colorCount: 3,
    matchLength: 4,
    virusCount: 20,
    virusTopRow: 6,
    spawn: Object.freeze({ x: 3, y: 0 }),
    previewSize: 1,
    pieceLimit: null,
    nextChoiceCount: 0,
    mask: null,
    biofilmCount: 0,
    boss: null,
    abilities: Object.freeze([]),
    infection: false,
    bacteriaColors: Object.freeze([2]),
    scoring: Object.freeze({
        virusPoints: 100,
        segmentPoints: 10,
        shieldPoints: 50,
        chainMultipliers: Object.freeze([1, 2, 3, 4, 5, 6]),
    }),
});
function assertInteger(name, value, min, max = Number.MAX_SAFE_INTEGER) {
    if (!Number.isInteger(value) || value < min || value > max) {
        throw new RangeError(`${name} must be an integer in [${min}, ${max}], got ${value}`);
    }
}
/** Проверяет босса: клетки внутри сосуда, ниже зоны появления, без пересечений; способности. */
function validateBoss(rules, isWall, occupied) {
    const boss = rules.boss;
    if (!boss)
        return;
    assertInteger('boss.hp', boss.hp, 1, 1000);
    assertInteger('boss.damage', boss.damage, 0, 1000);
    assertInteger('boss.chainBonus', boss.chainBonus, 0, 1000);
    assertInteger('boss.partDamage', boss.partDamage, 0, 1000);
    if ((boss.placement === 'vessel') !== (boss.body !== null)) {
        throw new RangeError('boss.body is required exactly for placement "vessel"');
    }
    if ((boss.placement === 'hybrid') !== boss.parts.length > 0) {
        throw new RangeError('boss.parts are required exactly for placement "hybrid"');
    }
    for (const p of [...bossBodyCells(boss), ...boss.parts]) {
        const key = `${p.x},${p.y}`;
        if (!Number.isInteger(p.x) ||
            !Number.isInteger(p.y) ||
            p.x < 0 ||
            p.x >= rules.width ||
            p.y <= rules.spawn.y + 1 ||
            p.y >= rules.height ||
            isWall(p.x, p.y) ||
            occupied.has(key)) {
            throw new RangeError(`boss cell (${p.x}, ${p.y}) must be inside the vessel, below spawn, unique`);
        }
        occupied.add(key);
    }
    for (const a of boss.abilities) {
        if (a.every !== null)
            assertInteger(`boss ${a.kind}.every`, a.every, 1, 1000);
        if (a.belowHp !== null)
            assertInteger(`boss ${a.kind}.belowHp`, a.belowHp, 0, boss.hp);
        assertInteger(`boss ${a.kind}.count`, a.count, 0, a.kind === 'fog' ? rules.width : 1000);
        assertInteger(`boss ${a.kind}.duration`, a.duration, 0, 1000);
        if ((a.kind === 'fog' || a.kind === 'storm' || a.kind === 'latency') && a.duration < 1) {
            throw new RangeError(`boss ${a.kind}.duration must be ≥ 1`);
        }
    }
}
/** Собирает правила из значений по умолчанию и переопределений и проверяет их согласованность. */
export function createRules(overrides = {}) {
    const rules = {
        ...DEFAULT_RULES,
        ...overrides,
        scoring: { ...DEFAULT_RULES.scoring, ...overrides.scoring },
    };
    assertInteger('width', rules.width, 2, 64);
    assertInteger('height', rules.height, 2, 64);
    assertInteger('colorCount', rules.colorCount, 1, 16);
    assertInteger('matchLength', rules.matchLength, 2, Math.max(rules.width, rules.height));
    assertInteger('spawn.x', rules.spawn.x, 0, rules.width - 2);
    assertInteger('spawn.y', rules.spawn.y, 0, rules.height - 1);
    assertInteger('virusTopRow', rules.virusTopRow, rules.spawn.y + 1, rules.height - 1);
    const isWall = (x, y) => rules.mask?.[y]?.[x] === '#';
    if (rules.mask) {
        if (rules.mask.length !== rules.height ||
            rules.mask.some((row) => row.length !== rules.width || /[^.#]/.test(row))) {
            throw new RangeError(`mask must be ${rules.height} rows of ${rules.width} characters '.' or '#'`);
        }
        if (isWall(rules.spawn.x, rules.spawn.y) || isWall(rules.spawn.x + 1, rules.spawn.y)) {
            throw new RangeError('spawn cells must be inside the vessel mask');
        }
    }
    const bossCells = new Set();
    if (rules.boss)
        validateBoss(rules, isWall, bossCells);
    let zoneCells = 0;
    for (let y = rules.virusTopRow; y < rules.height; y++) {
        for (let x = 0; x < rules.width; x++) {
            if (!isWall(x, y) && !bossCells.has(`${x},${y}`))
                zoneCells++;
        }
    }
    assertInteger('virusCount', rules.virusCount, 0, zoneCells);
    assertInteger('biofilmCount', rules.biofilmCount, 0, rules.virusCount);
    if (rules.boss)
        assertInteger('boss.guards', rules.boss.guards, 0, rules.virusCount);
    assertInteger('previewSize', rules.previewSize, 0, 8);
    if (rules.abilities.length > 2 || rules.abilities.some((a) => !ABILITY_KINDS.includes(a))) {
        throw new RangeError(`abilities must be at most 2 of ${ABILITY_KINDS.join(', ')}`);
    }
    if (rules.pieceLimit !== null) {
        assertInteger('pieceLimit', rules.pieceLimit, 1);
    }
    if (rules.nextChoiceCount !== 0) {
        assertInteger('nextChoiceCount', rules.nextChoiceCount, 2, 6);
        assertInteger('previewSize (при выборе капсулы)', rules.previewSize, 1, 8);
    }
    if (rules.scoring.chainMultipliers.length === 0) {
        throw new RangeError('scoring.chainMultipliers must not be empty');
    }
    return rules;
}
//# sourceMappingURL=rules.js.map